import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { Button } from "@/components/ui/button";
import { WhyAvolus } from "@/components/site/WhyAvolus";
import { RATING_VALUE, REVIEW_COUNT, GOOGLE_BUSINESS_URL, breadcrumbScript } from "@/lib/schema";

const description = "Why rent with Avolus Cars: free Vancouver and YVR delivery, filmed walkarounds, published CAD rates, the exact car you booked, and direct contact with the owners.";

export const Route = createFileRoute("/why-us")({
  head: () => ({
    meta: [
      { title: "Why Avolus | Luxury Car Rental Vancouver" },
      { name: "description", content: description },
      { property: "og:title", content: "Why Avolus | Luxury Car Rental Vancouver" },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://avoluscars.com/why-us" },
    ],
    links: [{ rel: "canonical", href: "https://avoluscars.com/why-us" }],
    scripts: [
      breadcrumbScript([
        { name: "Home", url: "https://avoluscars.com/" },
        { name: "Why Us", url: "https://avoluscars.com/why-us" },
      ]),
    ],
  }),
  component: WhyUs,
});

function WhyUs() {
  return (
    <Layout inner showWhyAvolus={false}>
      <section className="pt-40 pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Eyebrow>Why Us</Eyebrow>
          <h1 className="font-display text-5xl md:text-7xl leading-tight">
            Why drivers choose Avolus
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            We started Avolus because renting a nice car in Vancouver meant airport counters,
            surprise fees, and vehicles that did not match the photos. We built the rental we
            always wanted: the exact car you booked, delivered to your door, with every price
            published before you commit.
          </p>
        </div>
      </section>

      <WhyAvolus />

      <section className="py-16 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl mb-6">What that means for your rental</h2>
          </Reveal>
          <ul className="space-y-5 text-muted-foreground leading-relaxed">
            <li>
              <strong className="text-foreground">No counters, no shuttles.</strong> Your car
              arrives at your home, hotel, or YVR arrivals, and we collect it the same way.
            </li>
            <li>
              <strong className="text-foreground">No surprise charges.</strong> Rates, the $2,000
              refundable deposit, the $60/day LDW, and extra-kilometre pricing are all published
              on the <Link to="/rates" className="underline underline-offset-4">rates page</Link> in
              Canadian dollars.
            </li>
            <li>
              <strong className="text-foreground">No disputes over damage.</strong> Every delivery
              and collection is filmed, so you are never charged for damage you did not cause.
            </li>
            <li>
              <strong className="text-foreground">No bait and switch.</strong> The car in the
              listing is the car at your door, cleaned and fueled.
            </li>
            <li>
              <strong className="text-foreground">No call centres.</strong> You deal directly with
              the small team that owns and maintains the fleet.
            </li>
          </ul>
        </div>
      </section>

      <section className="py-16 border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <h2 className="font-display text-3xl md:text-4xl mb-6">Rated by our guests</h2>
          <p className="text-muted-foreground leading-relaxed">
            Avolus holds a {RATING_VALUE}-star rating across {REVIEW_COUNT} Google reviews. Read them on
            our{" "}
            <a href={GOOGLE_BUSINESS_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">
              Google listing
            </a>
            , or browse the fleet and see the cars for yourself.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Button asChild className="h-12 rounded-none shadow-none uppercase tracking-widest">
              <a href="/book">Book Now<ArrowRight aria-hidden="true" /></a>
            </Button>
            <Button asChild variant="outline" className="h-12 rounded-none shadow-none uppercase tracking-widest">
              <Link to="/fleet">View the Fleet</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
